'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useRef, useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { materialsCreateVoice } from '../apis';
import type { VoiceClonePhase } from '../types';

interface UseVoiceCloneSubmitOptions {
  /** 创建接口已成功受理（拿到 voice_id）后触发，用于刷新试用次数等 */
  onCreateAccepted?: () => void;
  /** 创建接口返回 voice_id 后触发，用于立刻展示「进行中」卡片 */
  onVoiceProcessingStart?: (payload: { voiceId: string }) => void;
}

interface UseVoiceCloneSubmitResult {
  phase: VoiceClonePhase;
  submit: (sourceUri: string, voiceName: string, useTrial: boolean) => Promise<void>;
  reset: () => void;
}

export function useVoiceCloneSubmit(options?: UseVoiceCloneSubmitOptions): UseVoiceCloneSubmitResult {
  const t = useTranslations('tools.voiceClone.submit');
  const onCreateAcceptedRef = useRef(options?.onCreateAccepted);
  onCreateAcceptedRef.current = options?.onCreateAccepted;
  const onVoiceProcessingStartRef = useRef(options?.onVoiceProcessingStart);
  onVoiceProcessingStartRef.current = options?.onVoiceProcessingStart;
  const [phase, setPhase] = useState<VoiceClonePhase>('idle');

  const updatePhase = useCallback((next: VoiceClonePhase) => {
    setPhase(next);
  }, []);

  const submit = useCallback(
    async (sourceUri: string, voiceName: string, useTrial: boolean) => {
      updatePhase('submitting');

      try {
        const { data } = await materialsCreateVoice({
          body: {
            name: voiceName,
            source_uri: sourceUri,
            use_trial: useTrial,
          },
        });

        const voiceId = data?.voice_id;
        if (!voiceId) {
          updatePhase('failed');
          toast({
            type: 'error',
            message: t('createFailed'),
          });
          return;
        }

        onCreateAcceptedRef.current?.();
        onVoiceProcessingStartRef.current?.({ voiceId });
        updatePhase('idle');
      } catch (_error) {
        updatePhase('failed');
        toast({
          type: 'error',
          message: t('createFailed'),
        });
      }
    },
    [updatePhase, t],
  );

  const reset = useCallback(() => {
    updatePhase('idle');
  }, [updatePhase]);

  return { phase, submit, reset };
}
